import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PublicOrderFormConfig } from "../../../../types/PublicOrderFormConfig";
import { SettingsRowProps } from "../../components/SettingsRow";
import { useWorkspace } from "../../../../context/WorkspaceContext";
import { useParameters } from "../../../../hooks";
import { ParameterType } from "../../../../types";
import { fetchPublicProducts } from "../../../../api/publicOrder";
import { useUserSession } from "../../../../context/UserContext";
import { bySortNr } from "../../../../utils/comparators";
import { ToggleSwitch } from "@components";
import { panelSm, sectionDivider } from "@design-system";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { HiOutlineMenu } from 'react-icons/hi';

interface ProductSettingsTabProps {
  SettingsRow: React.ComponentType<SettingsRowProps>;
  config: PublicOrderFormConfig;
  handleConfigChange: <K extends keyof PublicOrderFormConfig>(key: K, value: PublicOrderFormConfig[K]) => void;
}

// Simple sortable item component for products
const SortableProductItem: FC<{ product: any; index: number }> = ({ product, index }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: product.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`flex items-center justify-between px-3 py-2 cursor-grab active:cursor-grabbing hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 ${
        isDragging ? 'bg-brand-subtle dark:bg-brand-muted/20 border-brand-muted dark:border-brand shadow-lg transform scale-105' : ''
      }`}
    >
      <div className="flex items-center gap-3">
        <HiOutlineMenu className={`w-4 h-4 ${isDragging ? 'text-brand' : 'text-muted'}`} />
        <span className={`text-xs ${isDragging ? 'text-lgb-blue-600 dark:text-brand' : 'text-muted'}`}>#{index + 1}</span>
        <span className={`text-sm ${isDragging ? 'text-text font-medium' : 'text-secondary'}`}>
          {product.descriptionShort || product.description || `Product ${product.id}`}
        </span>
      </div>
    </li>
  );
};

// Simple sortable item component for categories
const SortableCategoryItem: FC<{ category: any; index: number }> = ({ category, index }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`flex items-center justify-between px-3 py-2 cursor-grab active:cursor-grabbing hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 ${
        isDragging ? 'bg-brand-subtle dark:bg-brand-muted/20 border-brand-muted dark:border-brand shadow-lg transform scale-105' : ''
      }`}
    >
      <div className="flex items-center gap-3">
        <HiOutlineMenu className={`w-4 h-4 ${isDragging ? 'text-brand' : 'text-muted'}`} />
        <span className={`text-xs ${isDragging ? 'text-lgb-blue-600 dark:text-brand' : 'text-muted'}`}>#{category.sortNr}</span>
        <span className={`text-sm ${isDragging ? 'text-text font-medium' : 'text-secondary'}`}>{category.name}</span>
      </div>
    </li>
  );
};

const ProductSettingsTab: FC<ProductSettingsTabProps> = ({ SettingsRow, config, handleConfigChange }) => {
  const { t } = useTranslation(["order_form", "common"]);
  const { activeWorkspace } = useWorkspace();
  const { userSession } = useUserSession();
  const categoriesQuery = useParameters(undefined, ParameterType.ProductType);
  const [availableCategories, setAvailableCategories] = useState<any[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [availableProducts, setAvailableProducts] = useState<any[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // DnD sensors for keyboard and pointer accessibility
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    const load = async () => {
      if (!activeWorkspace?.workspaceId || !userSession?.token?.accessToken) return;
      
      setIsLoadingCategories(true);
      setIsLoadingProducts(true);
      try {
        const categories = categoriesQuery.data ?? [];
        const products = await fetchPublicProducts(activeWorkspace.workspaceId, userSession.token.accessToken);
        const publicCategoryIds = [] as string[];
        const publicProducts = products.filter((p: any) => p?.isPublic);
        
        products.forEach((p: any) => {
          if (p?.isPublic && p?.productType) {
            if (Array.isArray(p.productType)) {
              publicCategoryIds.push(...p.productType);
            } else {
              publicCategoryIds.push(p.productType);
            }
          }
        });
        
        // Filter categories to only those with public products
        const available = categories.filter((c: any) => publicCategoryIds.includes(c.id));
        setAvailableCategories(available);
        
        // Set available public products
        setAvailableProducts(publicProducts);
        
        // Auto-sync product order: add any new public products to the end
        if (publicProducts.length > 0) {
          const currentOrder = config.productOrder || [];
          const currentProductIds = currentOrder.map(p => p.id);
          const newProducts = publicProducts.filter((p: any) => !currentProductIds.includes(p.id));
          
          if (newProducts.length > 0) {
            const maxSortNr = currentOrder.length > 0 ? Math.max(...currentOrder.map(p => p.sortNr)) : 0;
            const updatedOrder = [
              ...currentOrder,
              ...newProducts.map((p: any, index: number) => ({
                id: p.id,
                sortNr: maxSortNr + index + 1
              }))
            ];
            handleConfigChange("productOrder", updatedOrder);
          }
        }
        
        // Auto-sync: when enabling category filter, create filters for all available categories
        // When disabling, keep the filters but they won't be used
      } catch {
        // Handle error silently
      } finally {
        setIsLoadingCategories(false);
        setIsLoadingProducts(false);
        setInitialLoadComplete(true);
      }
    };
    load();
  }, [activeWorkspace?.workspaceId, userSession?.token?.accessToken, categoriesQuery.data, config.productOrder, handleConfigChange]);

  // When category filter is enabled, ensure all available categories are in the filter list
  useEffect(() => {
    if (!config.enableCategoryFilter || availableCategories.length === 0) {
      return;
    }

    const currentFilters = config.categoryFilters || [];
    const existingIds = new Set(currentFilters.map(f => f.id));
    const missingCategories = availableCategories.filter(c => !existingIds.has(c.id));
    
    // Fix existing categories that might have wrong name values (description instead of code)
    const existingFilters = currentFilters.map(filter => {
      const category = availableCategories.find(c => c.id === filter.id);
      if (!category) return filter; // Keep filter if category not found
      
      const expectedName = category.code || category.description;
      if (filter.name !== expectedName) {
        return { ...filter, name: expectedName };
      }
      return filter;
    });
    
    // Add missing categories
    const allFilters = [...existingFilters];
    if (missingCategories.length > 0) {
      const nextStartSort = currentFilters.length > 0 
        ? Math.max(...currentFilters.map(f => f.sortNr)) + 1 
        : 1;
      
      const newFilters = missingCategories.map((c, index) => ({
        id: c.id,
        name: c.code || c.description,
        sortNr: nextStartSort + index,
      }));
      
      allFilters.push(...newFilters);
    }
    
    // Only update if there are actual changes (safe deep comparison)
    const hasChanges = currentFilters.length !== allFilters.length ||
      currentFilters.some((current, index) => {
        const updated = allFilters[index];
        return !updated || 
               current.id !== updated.id || 
               current.name !== updated.name || 
               current.sortNr !== updated.sortNr;
      });
    
    if (hasChanges) {
      handleConfigChange("categoryFilters", allFilters);
    }
  }, [config.enableCategoryFilter, availableCategories, config.categoryFilters]);

  const sortedConfigured = useMemo(() => {
    return (config.categoryFilters || []).slice().sort(bySortNr);
  }, [config.categoryFilters]);

  // Memoized sorted products for product order
  const sortedProducts = useMemo(() => {
    const productOrder = config.productOrder || [];
    return productOrder
      .map(orderItem => {
        const product = availableProducts.find(p => p.id === orderItem.id);
        return product ? { ...product, sortNr: orderItem.sortNr } : null;
      })
      .filter(Boolean)
      .sort(bySortNr);
  }, [config.productOrder, availableProducts]);

  // Handle drag end for product reordering
  const handleProductDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = sortedProducts.findIndex(product => product.id === active.id);
      const newIndex = sortedProducts.findIndex(product => product.id === over?.id);
      
      const reorderedProducts = arrayMove(sortedProducts, oldIndex, newIndex);
      
      // Update the sortNr values based on new order
      const updatedProductOrder = reorderedProducts.map((product, index) => ({
        id: product.id,
        sortNr: index + 1
      }));
      
      handleConfigChange("productOrder", updatedProductOrder);
    }
  };

  // Handle drag end for category reordering
  const handleCategoryDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const sortedConfigured = (config.categoryFilters || []).sort(bySortNr);
      const oldIndex = sortedConfigured.findIndex(category => category.id === active.id);
      const newIndex = sortedConfigured.findIndex(category => category.id === over?.id);
      
      const reorderedCategories = arrayMove(sortedConfigured, oldIndex, newIndex);
      
      // Update the sortNr values based on new order
      const updatedCategoryOrder = reorderedCategories.map((category, index) => ({
        ...category,
        sortNr: index + 1
      }));
      
      handleConfigChange("categoryFilters", updatedCategoryOrder);
    }
  };

  return (
    <div className="space-y-6">
      {/* Show loading state only during initial fetch */}
      {!initialLoadComplete && (isLoadingCategories || isLoadingProducts) && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
          <span className="ml-3 text-sm text-muted">
            {isLoadingCategories && isLoadingProducts 
              ? t("order_form:loading.categories_and_products")
              : isLoadingCategories 
                ? t("order_form:loading.categories")
                : t("order_form:loading.products")
            }
          </span>
        </div>
      )}
      
      {/* Show content after initial load is complete */}
      {initialLoadComplete && (
        <div className="space-y-6">
          <div>
            <SettingsRow
              title={t("order_form:product_settings.enable_category_filter.title")}
              description={t("order_form:product_settings.enable_category_filter.description")}
              separator={false}
        >
          <div className="space-y-3">
            {/* Toggle: enable category filter */}
            <ToggleSwitch
              checked={!!config.enableCategoryFilter}
              onChange={() => handleConfigChange("enableCategoryFilter", !config.enableCategoryFilter)}
            />
          </div>
        </SettingsRow>
        
        {config.enableCategoryFilter && availableCategories.length > 0 && (
          <div className="space-y-4">  
            {/* Single list with all categories and drag-and-drop ordering */}
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleCategoryDragEnd}
            >
              <SortableContext
                items={sortedConfigured.map(c => c.id)}
                strategy={verticalListSortingStrategy}
              >
                <ul className={`divide-y divide-border ${panelSm} overflow-hidden`}>
                  {sortedConfigured.length === 0 && (
                    <li className="p-3 text-sm text-muted">{t("common:none", "None")}</li>
                  )}
                  {sortedConfigured.map((c, idx) => (
                    <SortableCategoryItem
                      key={c.id}
                      category={c}
                      index={idx}
                    />
                  ))}
                </ul>
              </SortableContext>
            </DndContext>
          </div>
        )}
        
        {config.enableCategoryFilter && availableCategories.length === 0 && !isLoadingCategories && (
          <SettingsRow
            title={t("order_form:product_settings.no_categories.title")}
            description={t("order_form:product_settings.no_categories.description")}
            separator={false}
          >
            <div className="text-sm text-muted">
              {t("order_form:product_settings.no_categories.message")}
            </div>
          </SettingsRow>
        )}
      </div>

      {/* Product Order Section */}
      <div>
        <div className={`${sectionDivider} pt-4 border-t-0`}></div>

        {/* Product Order List */}
        {availableProducts.length > 0 && (
          <div className="space-y-4">
            <div>
              <h4 className="text-base font-medium text-text">
                {t("order_form:product_settings.product_order.title")}
              </h4>
              <p className="text-sm text-muted mt-1">
                {t("order_form:product_settings.product_order.all_included")}
              </p>
            </div>
            
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleProductDragEnd}
            >
              <SortableContext
                items={sortedProducts.map(p => p.id)}
                strategy={verticalListSortingStrategy}
              >
                <ul className={`divide-y divide-border ${panelSm} overflow-hidden`}>
                  {sortedProducts.map((product, idx) => (
                    <SortableProductItem
                      key={product.id}
                      product={product}
                      index={idx}
                    />
                  ))}
                </ul>
              </SortableContext>
            </DndContext>
          </div>
        )}

        {/* No Products Message */}
        {availableProducts.length === 0 && !isLoadingProducts && (
          <SettingsRow
            title={t("order_form:product_settings.product_order.no_products.title")}
            description={t("order_form:product_settings.product_order.no_products.description")}
            separator={false}
          >
            <div className="text-sm text-muted">
              {t("order_form:product_settings.product_order.no_products.message")}
            </div>
          </SettingsRow>
        )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductSettingsTab;


